import { ReactNode, forwardRef, useCallback } from 'react';

import {
  Box,
  Card,
  CardActions,
  IconButton,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { useSnackbar, SnackbarContent, SnackbarAction } from 'notistack';

type SnackMessageProps = {
  id: string | number;
  message: string | ReactNode;
  action?: SnackbarAction;
};

const useStyles = makeStyles(
  ({ palette, breakpoints, spacing, transitions }) => ({
    root: {
      maxWidth: '448px !important',
      [breakpoints.up('sm')]: {
        minWidth: '344px !important',
      },
    },
    card: {
      width: '100%',
      backgroundColor: '#000',
      color: palette.getContrastText('#000'),
    },
    actionRoot: {
      padding: spacing(1, 1, 1, 2),
      justifyContent: 'space-between',
    },
    closeIcon: {
      color: palette.grey[500],
      marginLeft: 'auto',
      padding: spacing(1, 1),
      transform: 'rotate(0deg)',
      transition: transitions.create('transform', {
        duration: transitions.duration.shortest,
      }),
    },
  }),
);

const SnackMessage = forwardRef<HTMLDivElement, SnackMessageProps>(
  (props: SnackMessageProps, ref) => {
    const { id, message, action } = props;
    const classes = useStyles();
    const { closeSnackbar } = useSnackbar();

    const handleDismiss = useCallback(() => {
      closeSnackbar(id);
    }, [id, closeSnackbar]);

    return (
      <SnackbarContent ref={ref} className={classes.root}>
        <Card className={classes.card}>
          <CardActions classes={{ root: classes.actionRoot }}>
            <Typography variant="subtitle2">{message}</Typography>
            <Box display="flex" alignItems="center" gridGap={2}>
              {action && action}
              <IconButton className={classes.closeIcon} onClick={handleDismiss}>
                <CloseIcon />
              </IconButton>
            </Box>
          </CardActions>
        </Card>
      </SnackbarContent>
    );
  },
);

export default SnackMessage;
